import React, { useState } from 'react';
import Button from './Button';
import DropdownSelect from './DropdownSelect';
import { MATERIAL_FAMILY_LOOK, type MaterialFamily } from '../lib/materialFamily';
import { polymerGroupsFor } from '../lib/materialPolymers';
import { Icon } from './Icon';

export interface MaterialRatesCardProps {
    family: MaterialFamily;
    title: string;
    /** Shown at the right of the header when the card has no group picker ("€ / kg"). */
    unitLabel: string;
    /** Label of a group key (`common`, `polyamides`…). The tenant translates it;
     *  the Spanish-only admin maps it itself. */
    groupLabel: (key: string) => string;
    /** Accessible name of the group picker. */
    pickerLabel: string;
    /** One field per polymer — each app brings its own input. */
    renderField: (polymer: string) => React.ReactNode;
    /** True when that polymer's rate has a change not saved yet. A group that
     *  holds one says so in the picker, since its fields are out of sight. */
    isChanged?: (polymer: string) => boolean;
    /** «Sin guardar: {n}» — appended to a group's option when `n > 0`. */
    pendingLabel?: (n: number) => string;
    /** Phone: the card starts CLOSED and opens on tap (owner decision,
     *  2026-09-23). Closed it shows how many families it has, the first rates
     *  (`rateLabel`) and «sin guardar: N» if anything inside is unsaved. */
    collapsible?: boolean;
    /** «PLA 20» — the closed card's summary of one polymer. */
    rateLabel?: (polymer: string) => string;
}

/** How many rates the closed card's summary shows before «…». */
const SUMMARY_RATES = 3;

/**
 * The «default €/kg» card of one material family — ONE component for Tenant →
 * Configuración → Costes, the first-login assistant (the same
 * `CostDefaultsSection`) and Admin → Taller por defecto, so the three look the
 * same by construction.
 *
 * Filament has 29 families, so its fields go by group and a picker in the
 * header — where «€ / kg» sits on a card without groups — chooses which one is
 * shown (owner decision, 2026-09-23: no tabs, no scroll bars, no page full of
 * fields). Each option lists its families underneath, so nobody needs to know
 * that PVA lives in «Comunes» to find it. Resin (9) shows whole.
 *
 * Side by side (from `lg`, where both callers put filament and resin in two
 * columns) the card is always as tall as its LONGEST group (owner decision:
 * «Otros» has 12 fields, and the card must not grow and shrink as the picker
 * moves). Every group is rendered in the same grid cell and only the chosen
 * one is visible, so the height comes from the content itself rather than
 * from a guessed row height. Stacked (a phone), nothing sits beside it to
 * line up with, so the other groups are `hidden` and the card takes the
 * chosen group's height — reserving six rows there left up to four empty rows
 * between the filament fields and the resin card. Same component, same
 * markup: only the space the other groups reserve changes with the width.
 * Either way the hidden groups stay mounted (a half-typed value survives a
 * switch) and `inert`, out of the tab order and the accessibility tree.
 *
 * On a phone the callers pass `collapsible` (from `useIsMobile`, the same in
 * Configuración and in the assistant): the card starts closed, with a one-line
 * summary, and opens on tap — ten rows of rates are a long scroll for
 * something a workshop sets once.
 *
 * The caller wraps it in its own card surface.
 */
export default function MaterialRatesCard({
    family,
    title,
    unitLabel,
    groupLabel,
    pickerLabel,
    renderField,
    isChanged,
    pendingLabel,
    collapsible = false,
    rateLabel,
}: MaterialRatesCardProps) {
    const groups = polymerGroupsFor(family);
    const [groupKey, setGroupKey] = useState(groups[0].key);
    const [open, setOpen] = useState(false);
    const expanded = !collapsible || open;
    const look = MATERIAL_FAMILY_LOOK[family];
    const all = groups.flatMap((g) => g.polymers);
    const pendingTotal = isChanged ? all.filter(isChanged).length : 0;

    const options = groups.map((g) => {
        const pending = isChanged ? g.polymers.filter(isChanged).length : 0;
        const list = g.polymers.join(', ');
        return {
            value: g.key,
            label: groupLabel(g.key),
            sublabel: pending > 0 && pendingLabel ? `${list} · ${pendingLabel(pending)}` : list,
        };
    });

    // The family icon the whole app uses (pieces, stock, orders, printers) —
    // shown here so the legend is learnt once.
    const icon = (
        <span className={`inline-flex items-center justify-center w-7 h-7 rounded-lg shrink-0 ${look.chip}`} aria-hidden>
            <Icon name={look.icon} className="!text-[16px]" />
        </span>
    );
    const picker = groups.length > 1 ? (
        <DropdownSelect
            value={groupKey}
            onChange={setGroupKey}
            options={options}
            ariaLabel={pickerLabel}
            hideOptionIndicator
            align="end"
            dropdownWidth="w-80"
            wrapSublabels
            surface="transparent"
            className="w-44 shrink-0 font-normal"
        />
    ) : (
        <span className="text-[10px] font-bold uppercase tracking-wide text-text-sub dark:text-slate-400 shrink-0">
            {unitLabel}
        </span>
    );

    return (
        <div>
            <div className="flex items-center gap-2 min-h-[36px] text-sm font-semibold text-text-main dark:text-white">
                {collapsible ? (
                    <Button
                        variant="ghost"
                        className="flex-1 min-w-0 !justify-start !px-0 !h-auto !py-0 !font-semibold text-left hover:!bg-transparent hover:!scale-100"
                        aria-expanded={open}
                        onClick={() => setOpen((o) => !o)}
                    >
                        {icon}
                        <span className="min-w-0 truncate">{title}</span>
                        <span className="text-xs font-normal text-text-sub dark:text-slate-400 shrink-0">· {all.length}</span>
                        <Icon name="expand_more" className={`!text-[18px] text-slate-400 ml-auto shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
                    </Button>
                ) : (
                    <>
                        {icon}
                        <span className="flex-1 min-w-0 truncate">{title}</span>
                    </>
                )}
                {!collapsible && picker}
            </div>
            {collapsible && !open && (
                <p className="mt-1 text-xs text-text-sub dark:text-slate-400 truncate">
                    {rateLabel && all.slice(0, SUMMARY_RATES).map(rateLabel).join(' · ') + ' · …'}
                    {pendingTotal > 0 && pendingLabel && (
                        <span className="text-amber-600 dark:text-amber-400">{rateLabel ? ' · ' : ''}{pendingLabel(pendingTotal)}</span>
                    )}
                </p>
            )}
            {collapsible && open && groups.length > 1 && <div className="mt-3 flex justify-end">{picker}</div>}
            {expanded && (
            <div className="grid mt-3">
                {groups.map((g) => {
                    const hidden = g.key !== groupKey;
                    return (
                        <div
                            key={g.key}
                            className={`col-start-1 row-start-1 grid-cols-2 gap-3 content-start ${hidden ? 'hidden lg:grid lg:invisible' : 'grid'}`}
                            inert={hidden}
                        >
                            {g.polymers.map((p) => (
                                <React.Fragment key={p}>{renderField(p)}</React.Fragment>
                            ))}
                        </div>
                    );
                })}
            </div>
            )}
        </div>
    );
}
